import type {
  DoctorSlugResponse,
  DoctorSlugValidationResponse,
} from '@/common/apis/services/doctor/validateDoctorSlug';
import { isDoctorProfileSelfRedirect, normalizeDoctorProfileSlug } from './isDoctorProfileSelfRedirect';

export type FullProfileLookup = {
  data?: Record<string, unknown> | null;
  redirect?: { route: string; statusCode: number };
} | null;

export type DoctorSlugNavigation =
  | {
      type: 'redirect';
      destination: string;
      statusCode: number;
      reason: 'full_profile' | 'slug_validation';
    }
  | {
      type: 'render';
      validatedSlug: string;
      slugInfo: DoctorSlugValidationResponse | null;
      fullProfileData: Record<string, any> | null;
    }
  | { type: 'notFound' };

function isValidationSuccess(result: DoctorSlugResponse): result is DoctorSlugValidationResponse {
  return !('error' in result) && !('redirect' in result) && typeof result?.slug === 'string';
}

function usableRedirect(
  route: string | undefined,
  statusCode: number | undefined,
  currentSlug: string,
): { destination: string; statusCode: number } | null {
  if (!route || isDoctorProfileSelfRedirect(currentSlug, route)) return null;
  const code = statusCode && statusCode >= 300 && statusCode < 400 ? statusCode : 301;
  return { destination: route, statusCode: code };
}

/**
 * `/api/doctors/:slug` and `/api/full-profile/:slug` can disagree on the
 * canonical slug (alias points at a row with no master user). Live profile
 * payload / full-profile redirect wins so the page does not 404↔301 loop.
 */
export function resolveDoctorProfileSlugNavigation(args: {
  slug: string;
  slugValidation: DoctorSlugResponse | null;
  fullProfile: FullProfileLookup;
}): DoctorSlugNavigation {
  const { slug, slugValidation, fullProfile } = args;

  const fpRedirect = usableRedirect(fullProfile?.redirect?.route, fullProfile?.redirect?.statusCode, slug);
  const svRedirect =
    slugValidation && 'redirect' in slugValidation
      ? usableRedirect(slugValidation.redirect.route, slugValidation.redirect.statusCode, slug)
      : null;
  const svSuccess = slugValidation && isValidationSuccess(slugValidation) ? slugValidation : null;
  const fpData =
    fullProfile?.data && typeof fullProfile.data === 'object' ? (fullProfile.data as Record<string, any>) : null;

  if (fpRedirect) {
    return { type: 'redirect', ...fpRedirect, reason: 'full_profile' };
  }

  if (fpData) {
    const fromPayload = typeof fpData.slug === 'string' ? normalizeDoctorProfileSlug(fpData.slug) : '';
    return {
      type: 'render',
      validatedSlug: fromPayload || slug,
      slugInfo: svSuccess,
      fullProfileData: fpData,
    };
  }

  if (svSuccess) {
    return {
      type: 'render',
      validatedSlug: svSuccess.slug || slug,
      slugInfo: svSuccess,
      fullProfileData: null,
    };
  }

  if (svRedirect) {
    return { type: 'redirect', ...svRedirect, reason: 'slug_validation' };
  }

  if (slugValidation && 'error' in slugValidation) {
    return { type: 'notFound' };
  }

  return {
    type: 'render',
    validatedSlug: slug,
    slugInfo: null,
    fullProfileData: null,
  };
}

export function shouldLookupFullProfileForSlug(slug: string, slugValidation: DoctorSlugResponse | null): boolean {
  if (!slugValidation) return false;
  if ('error' in slugValidation) return true;
  if ('redirect' in slugValidation) {
    return !isDoctorProfileSelfRedirect(slug, slugValidation.redirect.route);
  }
  return false;
}
